import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Star, CheckCircle } from 'lucide-react';
import { ReviewCard } from '../components/ReviewCard';
import { BusinessReplyForm } from '../components/BusinessReplyForm';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BusinessReviewsPage = () => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [reviews, setReviews] = useState([]);
  const [pendingReplies, setPendingReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all'); // 'all' or 'pending'

  useEffect(() => {
    fetchReviews();
    fetchPendingReplies();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/business/${user.id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Error al kargar reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingReplies = async () => {
    try {
      const response = await axios.get(`${API}/reviews/business/${user.id}/pending-replies`);
      setPendingReplies(response.data);
    } catch (error) {
      console.error('Error fetching pending replies:', error);
    }
  };

  const handleReplySubmitted = () => {
    fetchReviews();
    fetchPendingReplies();
  };

  const displayedReviews = tab === 'pending' ? pendingReplies : reviews;

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-manrope font-bold text-3xl sm:text-4xl mb-2">
            Reviews de Klientes
          </h1>
          <p className="text-muted-foreground">
            Responde a tus klientes y konstruye konfianza
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
          >
            <div className="flex items-center gap-3 mb-2">
              <Star className="w-8 h-8 text-reward-gold" />
              <div>
                <p className="text-sm text-muted-foreground">Total Reviews</p>
                <p className="text-3xl font-bold">{reviews.length}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
          >
            <div className="flex items-center gap-3 mb-2">
              <MessageSquare className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">Pendientes</p>
                <p className="text-3xl font-bold">{pendingReplies.length}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
          >
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Respondidas</p>
                <p className="text-3xl font-bold">{reviews.length - pendingReplies.length}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b">
          <button
            onClick={() => setTab('all')}
            className={`px-4 py-2 font-medium transition-colors relative ${
              tab === 'all'
                ? 'text-local-sage'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-all"
          >
            Todas ({reviews.length})
            {tab === 'all' && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-local-sage"
              />
            )}
          </button>
          <button
            onClick={() => setTab('pending')}
            className={`px-4 py-2 font-medium transition-colors relative ${
              tab === 'pending'
                ? 'text-local-sage'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-pending"
          >
            Sin Responder ({pendingReplies.length})
            {tab === 'pending' && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-local-sage"
              />
            )}
          </button>
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-local-sage" />
          </div>
        ) : displayedReviews.length > 0 ? (
          <div className="space-y-4" data-testid="business-reviews-list">
            {displayedReviews.map((review) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card rounded-2xl border border-border/50 overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-sm mb-1">{review.kuponTitle}</p>
                      <p className="text-xs text-muted-foreground">
                        Review de {review.userName}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < review.rating
                              ? 'fill-reward-gold text-reward-gold'
                              : 'fill-none text-muted-foreground'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <p className="text-sm text-foreground mb-3">{review.comment}</p>
                  
                  {review.businessReply ? (
                    <div className="bg-muted/30 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Tu respuesta:</p>
                      <p className="text-sm">{review.businessReply.reply}</p>
                    </div>
                  ) : (
                    <BusinessReplyForm review={review} onReplySubmitted={handleReplySubmitted} />
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-card rounded-2xl border border-border/50">
            <MessageSquare className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {tab === 'pending' 
                ? 'No hay reviews pendientes de responder'
                : 'Aún no tienes reviews'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
